import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import AddIcon from "@mui/icons-material/AddOutlined";
import ArchiveIcon from "@mui/icons-material/Inventory2Outlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMoveOutlined";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { DataTable, type Column } from "@/components/data/data-table";
import { FilterSelect } from "@/components/data/filter-select";
import { ShowArchivedToggle } from "@/components/common/show-archived-toggle";
import { BulkActionBar } from "@/components/common/bulk-action-bar";
import { useRowSelection } from "@/components/common/use-row-selection";
import { MoveToWorkspaceDialog } from "@/components/common/move-to-workspace-dialog";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { EmptyState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useConfirm } from "@/components/feedback/confirm-dialog";
import { type PipelineSummary, useBulkPipelines, usePipelinesPage } from "@/api/pipelines";
import { useConnectorNames } from "@/api/connectors";
import { useTableParams } from "@/lib/table-params";

const STATUS_OPTIONS = ["ACTIVE", "DRAFT", "PAUSED", "ERROR"] as const;
const MODE_OPTIONS = ["BATCH", "MICRO_BATCH", "REALTIME"] as const;

export const Route = createFileRoute("/_app/pipelines/")({
  component: PipelinesList,
});

function PipelinesList() {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const params = useTableParams({ sort: { id: "updatedAt", dir: "desc" } });
  const { data, isLoading, error, refetch } = usePipelinesPage(params);
  const connectorNames = useConnectorNames();
  const sel = useRowSelection();
  const bulk = useBulkPipelines();
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);

  async function runBulk(
    op: "archive" | "unarchive" | "delete" | "move",
    workspaceId?: string | null,
  ) {
    const res = await bulk.mutateAsync({ ids: sel.ids, op, workspaceId });
    sel.clear();
    toast.success(
      res.failed.length ? `${res.ok} done, ${res.failed.length} failed` : `${res.ok} updated`,
    );
  }

  const columns: Column<PipelineSummary>[] = [
    { id: "name", header: "Name", cell: (r) => r.name, sortValue: (r) => r.name },
    { id: "status", header: "Status", cell: (r) => <StatusChip status={r.status} /> },
    {
      id: "executionMode",
      header: "Mode",
      cell: (r) => r.executionMode ?? "BATCH",
      nowrap: true,
      sortValue: (r) => r.executionMode,
    },
    {
      id: "flow",
      header: "Source → Destination",
      cell: (r) =>
        `${connectorNames[r.sourceConnectorId ?? ""] ?? "—"} → ${
          connectorNames[r.destConnectorId ?? ""] ?? "—"
        }`,
      nowrap: true,
    },
    {
      id: "updatedAt",
      header: "Updated",
      cell: (r) => <RelativeTime value={r.updatedAt} />,
      sortValue: (r) => r.updatedAt,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Pipelines"
        description="Configurable ETL flows — source, transforms, destination."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate({ to: "/pipelines/new" })}
          >
            New pipeline
          </Button>
        }
      />

      <BulkActionBar
        count={sel.count}
        onClear={sel.clear}
        actions={[
          {
            label: "Move to workspace…",
            icon: <DriveFileMoveIcon fontSize="small" />,
            onClick: () => setBulkMoveOpen(true),
          },
          {
            label: "Archive",
            icon: <ArchiveIcon fontSize="small" />,
            onClick: () => runBulk("archive"),
          },
          {
            label: "Delete",
            icon: <DeleteIcon fontSize="small" />,
            danger: true,
            onClick: async () => {
              if (
                await confirm({
                  title: `Delete ${sel.count} pipeline${sel.count === 1 ? "" : "s"}?`,
                  destructive: true,
                })
              ) {
                await runBulk("delete");
              }
            },
          },
        ]}
      />

      <Card>
        <DataTable
          columns={columns}
          data={data?.content}
          total={data?.totalElements}
          params={params}
          selection={sel.api}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetch()}
          rowKey={(r) => r.id}
          searchPlaceholder="Search pipelines…"
          filters={
            <>
              <FilterSelect
                label="Status"
                value={params.filters.status ?? ""}
                onChange={(v) => params.setFilter("status", v)}
                options={[...STATUS_OPTIONS]}
              />
              <FilterSelect
                label="Mode"
                value={params.filters.mode ?? ""}
                onChange={(v) => params.setFilter("mode", v)}
                options={[...MODE_OPTIONS]}
              />
              <ShowArchivedToggle params={params} />
            </>
          }
          onRowClick={(r) =>
            navigate({ to: "/pipelines/$pipelineId", params: { pipelineId: r.id } })
          }
          empty={
            <EmptyState
              title="No pipelines yet"
              description="Create your first pipeline to move data between systems."
              action={
                <Button variant="contained" onClick={() => navigate({ to: "/pipelines/new" })}>
                  New pipeline
                </Button>
              }
            />
          }
        />
      </Card>

      <MoveToWorkspaceDialog
        open={bulkMoveOpen}
        onClose={() => setBulkMoveOpen(false)}
        noun={`${sel.count} pipeline${sel.count === 1 ? "" : "s"}`}
        currentWorkspaceId={null}
        onMove={(workspaceId) => runBulk("move", workspaceId)}
      />
    </PageContainer>
  );
}
